import React, {useEffect, useMemo, useState} from 'react';
import {Grid, useTheme, useMediaQuery} from "@mui/material";
import TextInput from "../Inputs/TextInput";
import {Button} from "@material-ui/core";
import useServiceUrls from "../../Utils/UseServiceUrls";
import useDeviceProfile from "../../Utils/UseDeviceProfile";
import {closeWindow, getQueryString, mapServiceUrls, onSnippetComplete} from "../../Helpers/Globals";
import ClickableRowTable from "../Tables/ClickableRowTable";
import useSearchCustomer from "../../Utils/UseSearchCustomer";
import queryString from "query-string";
import useSetCartCustomer from "../../Utils/UseSetCartCustomer";
import { getDisplayValue } from '../../Helpers/Globals';
import dayjs from 'dayjs';

/** Matches legacy clubMembershipsStatusName / IsActiveMemberships behavior (Status: 1 active, 2 inactive, 3 closed, 5 loyalty-only). */
function sortClubMembershipDates(a, b) {
    if (a != null && b != null) {
        return new Date(a).getTime() - new Date(b).getTime();
    }
    return 0;
}

function isActiveMemberships(clubMemberships) {
    const dates = [];
    let expirationSuffix = '';
    let isActive = false;
    clubMemberships.forEach((item) => {
        if (item.Status == "Active") {
            isActive = true;
        }
        if (item.Status == "Active" && item.ExpirationDate != null && item.ExpirationDate !== '') {
            dates.push(dayjs(item.ExpirationDate).format('MM/DD/YYYY'));
        }
    });
    if (dates.length > 0) {
        const sorted = dates.slice().sort(sortClubMembershipDates);
        expirationSuffix = ' - ' + sorted[0];
        isActive = true;
    }
    return { isActive, expirationSuffix };
}

function isLoyaltyOnlyClubMemberships(clubMemberships) {
    let isLoyaltyOnly = false;
    clubMemberships.forEach((item) => {
        if (item.Status == 5) {
            isLoyaltyOnly = true;
        }
    });
    return isLoyaltyOnly;
}

function isInactiveClubMemberships(clubMemberships) {
    let isInactive = false;
    clubMemberships.forEach((item) => {
        if (item.Status == 2) {
            isInactive = true;
        }
    });
    return isInactive;
}

function isClosedClubMemberships(clubMemberships) {
    let isClosed = false;
    clubMemberships.forEach((item) => {
        if (item.Status == 3) {
            isClosed = true;
        }
    });
    return isClosed;
}

function clubMembershipStatusName(clubMemberships) {
    if (!Array.isArray(clubMemberships) || clubMemberships.length === 0) {
        return '';
    }
    const { isActive, expirationSuffix } = isActiveMemberships(clubMemberships);
    if (isActive) {
        return 'Active' + expirationSuffix;
    }
    if (isLoyaltyOnlyClubMemberships(clubMemberships)) {
        return 'LoyaltyOnly';
    }
    if (isInactiveClubMemberships(clubMemberships)) {
        return 'Inactive';
    }
    if (isClosedClubMemberships(clubMemberships)) {
        return 'Closed';
    }
    return '';
}

function loyaltyProgramStatusName(loyaltyProgramMemberships) {
    return loyaltyProgramMemberships == null || loyaltyProgramMemberships.length === 0
        ? ''
        : 'Active';
}

export default function FindCustomer(props) {
    const { allowExact = false } = props;
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [sessionKey, setSessionKey] = useState('');
    const [loginDomain, setLoginDomain] = useState('');
    const [cartUid, setCartUid] = useState('');
    const [cartExternalId, setCartExternalId] = useState('');
    const [cartSourceExternalId, setCartSourceExternalId] = useState('');
    const [returnUrl, setReturnUrl] = useState('')
    const [isExactMatch, setIsExactMatch] = useState(false)
    const [customerUid, setCustomerUid] = useState('')
    const { serviceUrlsStatus, serviceUrls, serviceUrlsError } = useServiceUrls(loginDomain);
    const {status, deviceProfile, error} = useDeviceProfile(mapServiceUrls(serviceUrls), sessionKey);
    
    const [search, setSearch] = useState('')
    const [selectedCustomer, setSelectedCustomer] = useState('')
    const[customer, setCustomer] = useState()
    
    
    const {status: searchStatus, searchResults, error: searchError, refetch} = useSearchCustomer(serviceUrls, sessionKey, search)
    const setCartCustomer = useSetCartCustomer()

    useEffect(() => {
        setSessionKey(getQueryString(window.location.href, 'bl_sk') ?? getQueryString(window.location.href,'SessionKey') ?? '');
        setLoginDomain(getQueryString(window.location.href, 'bl_LoginDomain') ?? getQueryString(window.location.href,'LoginDomain') ?? '');
        setReturnUrl(getQueryString(window.location.href, 'bl_rurl') ?? getQueryString(window.location.href,'ReturnUrl') ?? '')
        setCartUid(getQueryString(window.location.href, 'bL_CartUid') ?? getQueryString(window.location.href,'CartUid') ?? '');
        setCartExternalId(getQueryString(window.location.href, 'bl_CartExternalId') ?? getQueryString(window.location.href,'cartExternalId') ?? '')
        setCartSourceExternalId(getQueryString(window.location.href, 'bl_CartSourceExternalId') ?? getQueryString(window.location.href,'CartSourceExternalId') ?? '')
    }, [window.location.search])

    const snippetDone = (selected) => {
        console.log(props.close)
        if (props.close !== undefined)
            props.close(selected ?? customer)
        else
            onSnippetComplete(returnUrl)
    }

    useEffect(() => {
        if (setCartCustomer.status === 'success') {
            snippetDone()
        }
    }, [setCartCustomer.status])

    const handleChange = (field, value) => {
        setSearch(value)
    }
    
    const findCustomer = (event) => {
        event.preventDefault()
        refetch()
    }
    
    // Normalize phone number by extracting only digits
    const normalizePhone = (phone) => {
        if (!phone) return '';
        return phone.replace(/\D/g, '');
    }

    // Check if search term appears to be a phone number (contains digits)
    const isPhoneSearch = (searchTerm) => {
        if (!searchTerm) return false;
        const digitsOnly = normalizePhone(searchTerm);
        // Consider it a phone search if it has at least 4 digits (partial phone)
        return digitsOnly.length >= 4;
    }

    // Validate exact match for phone numbers
    const isValidExactPhoneMatch = (searchTerm, customer) => {
        const normalizedSearch = normalizePhone(searchTerm);
        const customerMobile = normalizePhone(customer.MobilePhone);
        const customerPhone1 = normalizePhone(customer.Phone1);
        
        // For exact match, require full 10-digit match
        if (normalizedSearch.length !== 10) {
            return false;
        }
        
        // Check if search matches either phone field exactly
        return normalizedSearch === customerMobile || normalizedSearch === customerPhone1;
    }

    const findExactMatch = (event) => {
        event.preventDefault()
        setIsExactMatch(true)
        refetch()
    }
    
    // Auto-select customer if only one result and allowExact is true
    useEffect(() => {
        if (allowExact && isExactMatch && searchResults && searchResults.length === 1) {
            const singleCustomer = searchResults[0];
            
            // If search appears to be a phone number, validate exact match
            if (isPhoneSearch(search)) {
                if (!isValidExactPhoneMatch(search, singleCustomer)) {
                    // Not an exact match - reset exact match flag and don't auto-select
                    setIsExactMatch(false);
                    return;
                }
            }
            
            // Valid exact match - proceed with auto-selection
            setCustomer(singleCustomer);
            setSelectedCustomer(singleCustomer.Uid);
            // Auto-set the customer to cart if there's a cart
            if (hasCart()) {
                setCartCustomer.mutate({urls: serviceUrls, cartUid, cartExternalId, cartSourceExternalId, customer: singleCustomer, accessKey: sessionKey});
            } else {
                snippetDone(singleCustomer);
            }
        }
        if (isExactMatch && searchResults && searchResults.length > 1) {
            setIsExactMatch(false)
        }
    }, [searchResults, allowExact, search]);
    
    const hasCart = () => {
        return !cartUid.isEmpty() || !cartExternalId.isEmpty() || !cartSourceExternalId.isEmpty()
    }
    
    const setCustomerToCart = (event) => {
        event.preventDefault();
        console.log("Set customer to cart")
        const customer = searchResults.find(c => c.Uid === selectedCustomer)
        // Also call with full customer object if prop accepts it (for QuickLink compatibility)
        if (props.onCustomerSelectedFull !== undefined)
            props.onCustomerSelectedFull(customer)
        if (hasCart())
            setCartCustomer.mutate({urls: serviceUrls, cartUid, cartExternalId, cartSourceExternalId, customer, accessKey: sessionKey})
        else
            snippetDone()
    }

    const onSearchTableRowClick = customer => {
        if (props.onCustomerSelected !== undefined)
            props.onCustomerSelected(customer)
        

        if (selectedCustomer === customer.Uid) {
            setSelectedCustomer('')
        } else {
            console.log('customer', customer)
            setCustomer(customer)
            setSelectedCustomer(customer.Uid)
        }
    }

    const onSearchTableRowDoubleClick = customer => {
        setCustomer(customer)
        setCartCustomer.mutate({urls: serviceUrls, cartUid, cartExternalId, cartSourceExternalId, customer, accessKey: sessionKey})
    }

    const handleQuickSetCommand = (quickSetCommand) => {
        // Create a customer object from the quick set command
        const customer = {
            Uid: quickSetCommand.CustomerUid,
            // We'll need to fetch the full customer details or use what's available
        };
        
        setCustomer(customer);
        
        if (hasCart()) {
            setCartCustomer.mutate({urls: serviceUrls, cartUid, cartExternalId, cartSourceExternalId, customer, accessKey: sessionKey});
        } else {
            snippetDone()
        }
    }

    const customerColumns = useMemo(() => {
        // If device profile is available and has CustomerSearchColumns, use them
        if (deviceProfile?.POSSnippetSettings?.CustomerSearchColumns) {
            return deviceProfile.POSSnippetSettings.CustomerSearchColumns.map((columnName, index) => {
                // Map the column names to their corresponding accessors
                const accessorMap = {
                    'AccountNumber': 'Code',
                    'FirstName': 'FirstName',
                    'LastName': 'LastName',
                    'Company': 'CompanyName',
                    'EmailAddress': 'EmailAddress'
                };

                if (columnName === 'ClubMembershipStatus') {
                    return {
                        Header: getDisplayValue(columnName),
                        id: 'ClubMembershipStatus',
                        accessor: (row) => clubMembershipStatusName(row.ClubMemberships),
                    };
                }

                if (columnName === 'LoyaltyProgramStatus') {
                    return {
                        Header: getDisplayValue(columnName),
                        id: 'LoyaltyProgramStatus',
                        accessor: (row) => loyaltyProgramStatusName(row.LoyaltyProgramMemberships),
                    };
                }

                return {
                    Header: getDisplayValue(columnName),
                    accessor: accessorMap[columnName] || columnName,
                };
            });
        }
        
        // Fallback to the original hardcoded columns
        return [
            {
                Header: 'Account Number',
                accessor: 'Code',
            },
            {
                Header: 'First Name',
                accessor: 'FirstName',
            },
            {
                Header: 'Last Name',
                accessor: 'LastName',
            },
            {
                Header: 'Email',
                accessor: 'EmailAddress',
            },
            {
                Header: 'Mobile Phone',
                accessor: 'MobilePhone',
            },
        ];
    }, [deviceProfile])
    
    return (
        <>
            {deviceProfile?.POSSnippetSettings?.QuickSetCommands && deviceProfile.POSSnippetSettings.QuickSetCommands.length > 0 && (
                <>
                    <div style={{
                        marginBottom: '15px', 
                        textAlign: 'left',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        padding: '10px',
                        width: '100%',
                        backgroundColor: '#f9f9f9'
                    }}>
                        {deviceProfile.POSSnippetSettings.QuickSetCommands.map((quickSetCommand, index) => (
                            <Button
                                key={index}
                                style={{margin: '5px', height: '60px'}}
                                variant='contained'
                                color='primary'
                                onClick={() => handleQuickSetCommand(quickSetCommand)}
                            >
                                {quickSetCommand.Caption}
                            </Button>
                        ))}
                    </div>
                </>
            )}
            <form onSubmit={selectedCustomer ? setCustomerToCart : findCustomer} >
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={allowExact ? 8 : 9} md={allowExact ? 9 : 10}>
                        <TextInput required={false} id={'Customer-Search'} value={search} label={'Last Name, First/Last Name Combination, Email, Account Number, Phone Number'} variant={'outlined'} width={'100%'}
                                   autoFocus={true} handleChange={handleChange}/>
                    </Grid>
                    <Grid item xs={12} sm={allowExact ? 4 : 3} md={allowExact ? 3 : 2}>
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: 'row', 
                            gap: '4px', 
                            marginTop: isSmallScreen ? '10px' : '0px', 
                            justifyContent: isSmallScreen ? 'center' : 'flex-end',
                            alignItems: 'center',
                            height: '100%'
                        }}>
                            <Button type={'submit'} variant='contained' color={'primary'}>{selectedCustomer ? 'Set' : 'Search'}</Button>
                            {allowExact && <Button 
                                type={'button'} 
                                variant='contained' 
                                color={'secondary'}
                                onClick={findExactMatch}
                                style={{
                                    whiteSpace: 'nowrap',
                                    minWidth: isSmallScreen ? 'auto' : '120px'
                                }}
                            >
                                {isSmallScreen ? 'Exact' : 'Exact Match'}
                            </Button>}
                        </div>
                    </Grid>
                </Grid>
            </form>
            {searchResults &&
                <>
                    <br />
                    {/*<p className={'bold left'}>Select Coupons to Apply</p>*/}
                    <div className={`table-wrapper-div`}>
                        <ClickableRowTable sortable={true} columns={customerColumns} data={searchResults} hideHeader={false} onRowClick={onSearchTableRowClick} onDoubleClick={onSearchTableRowDoubleClick} selected={selectedCustomer}/>
                    </div>
                </>
            }
            {serviceUrlsStatus === 'error' && <p className='error-text'>{serviceUrlsError}</p>}
        </>
    )
}